import apiClient from './apiClient';
import { decryptField } from '../utils/encryption';

/**
 * Report API Service
 * Handles fetching tabular report data from the backend
 */

/**
 * Fetch raw tabular data (events/reports) for all consented patients
 * @param {Object} options - Query options
 * @param {string} options.startDate - Start date (YYYY-MM-DD) - optional
 * @param {string} options.endDate - End date (YYYY-MM-DD) - optional
 * @param {string} options.hae - HAE type filter (1, 2, 3, or 'all') - optional
 * @param {string} options.gender - Gender filter (male, female, or 'all') - optional
 * @param {string} options.age - Age filter (infant, child, adult, or 'all') - optional
 * @param {number} options.limit - Number of records per page - optional
 * @param {number} options.skip - Number of records to skip - optional
 * @returns {Promise<Object>} { total, data }
 */
export const fetchTabularData = async (options = {}) => {
  try {
    const { startDate, endDate, hae, gender, age, limit, skip, patientIds } = options;

    const params = {};

    // Add pagination parameters (no $ prefix for get() requests)
    if (limit) params.limit = limit;
    if (skip !== undefined) params.skip = skip;

    // Add optional filters
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (hae && hae !== 'all') params.hae = hae;
    if (gender && gender !== 'all') params.gender = gender;
    if (age && age !== 'all') params.age = age;
    if (patientIds && patientIds.length > 0) params.patientIds = patientIds;

    console.log('[reportService] Fetching tabular data with params:', params);

    const response = await apiClient.get('/api/medical-reports/raw', { params });

    console.log('[reportService] Received response:', {
      total: response.data.total,
      dataLength: response.data.data?.length
    });

    // Decrypt patient names in the data
    const decryptedData = response.data.data.map(event => {
      // Decrypt patient name
      let decryptedName = '[Encrypted]';
      if (event.patient?.name) {
        try {
          decryptedName = decryptField(event.patient.name);
        } catch (err) {
          console.error('Failed to decrypt patient name:', err);
          decryptedName = '[Decryption Error]';
        }
      }

      return {
        ...event,
        patient: {
          ...event.patient,
          name: decryptedName,
        },
      };
    });

    return {
      total: response.data.total || 0,
      data: decryptedData,
    };
  } catch (error) {
    console.error('Failed to fetch tabular data:', error);

    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please login again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. Only medical administrators can access tabular data.');
    } else if (error.response?.status === 404) {
      throw new Error('Report service not found.');
    } else if (error.response?.status === 400) {
      throw new Error(error.response?.data?.message || 'Invalid request parameters.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to fetch tabular data. Please try again.');
    }
  }
};

/**
 * Fetch ALL tabular data for CSV export (no pagination)
 * @param {Object} options - Query options (same as fetchTabularData but without pagination)
 * @returns {Promise<Array>} All filtered data
 */
export const fetchAllTabularDataForCSV = async (options = {}) => {
  try {
    const { startDate, endDate, hae, gender, age, patientIds } = options;

    const params = {};

    // Add optional filters (no limit/skip for CSV export - get ALL data)
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (hae && hae !== 'all') params.hae = hae;
    if (gender && gender !== 'all') params.gender = gender;
    if (age && age !== 'all') params.age = age;
    if (patientIds && patientIds.length > 0) params.patientIds = patientIds;

    console.log('[reportService] Fetching ALL tabular data for CSV export with params:', params);

    const response = await apiClient.get('/api/medical-reports/raw', { params });

    console.log('[reportService] Received CSV export data:', {
      total: response.data.total,
      dataLength: response.data.data?.length
    });

    // Decrypt patient names in the data
    const decryptedData = response.data.data.map(event => {
      // Decrypt patient name
      let decryptedName = '[Encrypted]';
      if (event.patient?.name) {
        try {
          decryptedName = decryptField(event.patient.name);
        } catch (err) {
          console.error('Failed to decrypt patient name:', err);
          decryptedName = '[Decryption Error]';
        }
      }

      return {
        ...event,
        patient: {
          ...event.patient,
          name: decryptedName,
        },
      };
    });

    return decryptedData;
  } catch (error) {
    console.error('Failed to fetch all tabular data for CSV:', error);

    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please login again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. Only medical administrators can access tabular data.');
    } else if (error.response?.status === 404) {
      throw new Error('Report service not found.');
    } else if (error.response?.status === 400) {
      throw new Error(error.response?.data?.message || 'Invalid request parameters.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to fetch tabular data. Please try again.');
    }
  }
};

/**
 * Export tabular data to CSV
 * @param {Array} data - Array of event data
 * @param {string} filename - Filename for download
 */
export const exportToCSV = (data, filename = 'tabular_data.csv') => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  // Helper: Convert seconds to timestamp format (x Days xxh:xxm:xxs)
  const secondsToReadable = (seconds) => {
    if (!seconds) return 'N/A';

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const paddedHours = hours.toString().padStart(2, '0');
    const paddedMinutes = minutes.toString().padStart(2, '0');
    const paddedSeconds = secs.toString().padStart(2, '0');

    return `${days} Days ${paddedHours}h:${paddedMinutes}m:${paddedSeconds}s`;
  };

  // Helper: Convert gender abbreviation (M/F) - matches Mi Monitor
  const formatGender = (gender) => {
    if (!gender || gender === 'N/A' || gender.toLowerCase() === 'n/a') return '';
    const g = gender.toLowerCase();
    if (g === 'male' || g === 'm') return 'M';
    if (g === 'female' || g === 'f') return 'F';
    return '';
  };

  // Helper: Convert text to Title Case
  const toTitleCase = (text) => {
    if (!text) return '';
    if (text.toUpperCase() === 'N/A') return 'N/A';
    return text
      .replace(/,\s*/g, ', ')
      .toLowerCase()
      .split(' ')
      .map(word => {
        if (word.toLowerCase() === 'n/a') return 'N/A';
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  // Helper: Format array to comma-separated Title Case
  const formatArray = (arr) => {
    if (!arr || arr.length === 0) return 'N/A';
    const joined = Array.isArray(arr) ? arr.join(', ') : String(arr);
    return toTitleCase(joined) || 'N/A';
  };

  // Helper: Format empty values as N/A
  const formatEmpty = (value) => {
    if (value === null || value === undefined || value === '' || value === 'N/A') return 'N/A';
    return value;
  };

  // Helper: Check if value is N/A or None (case-insensitive)
  const isNA = (value) => {
    if (!value) return true;
    const v = value.toString().toLowerCase().trim();
    return v === 'n/a' || v === 'none' || v === '';
  };

  // Helper: Format value or return N/A
  const formatOrNA = (value, formatter = (v) => v) => {
    if (isNA(value)) return 'N/A';
    return formatter(value);
  };

  // Helper: Convert HAE type to Roman numerals
  const toRomanNumeral = (haeType) => {
    if (!haeType) return '';
    const type = haeType.toString().toLowerCase().replace(/type\s*/i, '').trim();
    const romanMap = {
      '1': 'I', 'i': 'I',
      '2': 'II', 'ii': 'II',
      '3': 'III', 'iii': 'III'
    };
    return romanMap[type] || type.toUpperCase();
  };

  // Define CSV headers - matches Mi Monitor column names
  const headers = [
    'Event Date',
    'Event Type',
    'Attack Duration',
    'Medication (HAE)',
    'Medication (HAE Dose)',
    'Other Medication',
    'Treatment Location',
    'Medication Injection Site',
    'Attack Severity',
    'Days of Work / School Missed',
    'Area Affected',
    'Triggers',
    'Time from Attack to Treatment',
    'Signs and Symptoms',
    'Patient Gender',
    'Patient Age',
    'Type of HAE',
    'Date of Diagnosis',
    'Physician Name',
    'Patient ID',
    'Physician Email',
  ].join(',');

  // Transform and format data to match Mi Monitor
  const transformedData = data.map(item => {
    // Parse attack duration (convert from string like "1d 2h 3m" to seconds)
    let durationSeconds = 0;
    if (item.attackDuration) {
      if (typeof item.attackDuration === 'number') {
        durationSeconds = item.attackDuration;
      } else if (typeof item.attackDuration === 'string') {
        // Parse days
        const daysMatch = item.attackDuration.match(/(\d+)d/);
        if (daysMatch) durationSeconds += parseInt(daysMatch[1]) * 86400;

        // Parse hours
        const hoursMatch = item.attackDuration.match(/(\d+)h/);
        if (hoursMatch) durationSeconds += parseInt(hoursMatch[1]) * 3600;

        // Parse minutes
        const minutesMatch = item.attackDuration.match(/(\d+)m/);
        if (minutesMatch) durationSeconds += parseInt(minutesMatch[1]) * 60;

        // Parse seconds
        const secondsMatch = item.attackDuration.match(/(\d+)s/);
        if (secondsMatch) durationSeconds += parseInt(secondsMatch[1]);
      }
    }

    // Time to treatment - convert from "xxh:xxm:xxs" format to padded format
    const convertTimeToTreatment = (timeStr) => {
      if (!timeStr || timeStr === 'N/A' || timeStr === '00h:00m:00s') return 'N/A';

      let totalSeconds = 0;

      // Parse days
      const daysMatch = timeStr.match(/(\d+)d/);
      if (daysMatch) totalSeconds += parseInt(daysMatch[1]) * 86400;

      // Parse hours (both "2h" and "02h" formats)
      const hoursMatch = timeStr.match(/(\d+)h/);
      if (hoursMatch) totalSeconds += parseInt(hoursMatch[1]) * 3600;

      // Parse minutes (both "5m" and "05m" formats)
      const minutesMatch = timeStr.match(/(\d+)m/);
      if (minutesMatch) totalSeconds += parseInt(minutesMatch[1]) * 60;

      // Parse seconds (both "5s" and "05s" formats)
      const secondsMatch = timeStr.match(/(\d+)s/);
      if (secondsMatch) totalSeconds += parseInt(secondsMatch[1]);

      if (totalSeconds <= 0) return 'N/A';

      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const paddedHours = hours.toString().padStart(2, '0');
      const paddedMinutes = minutes.toString().padStart(2, '0');
      const paddedSeconds = seconds.toString().padStart(2, '0');

      return `${paddedHours}h:${paddedMinutes}m:${paddedSeconds}s`;
    };

    return {
      eventDate: item.eventDate || '',
      eventType: item.eventType ? item.eventType.charAt(0).toUpperCase() + item.eventType.slice(1).toLowerCase() : '',
      attackDuration: secondsToReadable(durationSeconds),
      medicationHAE: formatOrNA(item.medicationHAE),
      medicationHAEDose: formatOrNA(item.medicationHAEDose, (v) => v.toLowerCase().replace(/\s+/g, '-')),
      otherMedication: formatOrNA(item.otherMedication, (v) => v.toLowerCase()),
      treatmentLocation: item.treatmentLocation ? toTitleCase(item.treatmentLocation) : '',
      injectionSite: formatOrNA(item.medicationInjectionSite, (v) => toTitleCase(v)),
      attackSeverity: item.attackSeverity && item.attackSeverity !== 'N/A' ? `${item.attackSeverity}.` : 'N/A',
      daysMissed: (() => {
        // Default to 0 for attack events
        const eventType = (item.eventType || '').toLowerCase();
        if (eventType === 'attack') {
          return item.missedDays !== null && item.missedDays !== undefined ? item.missedDays : 0;
        }
        return isNA(item.missedDays) ? 'N/A' : item.missedDays;
      })(),
      areaAffected: formatArray(item.areaAffected),
      triggers: formatArray(item.triggers),
      timeToTreatment: convertTimeToTreatment(item.timeToTreatment),
      signsAndSymptoms: formatArray(item.signsAndSymptoms),
      patientGender: formatEmpty(formatGender(item.patientGender)),
      patientAge: formatEmpty(item.patientAge),
      typeOfHAE: formatEmpty(toRomanNumeral(item.typeOfHAE)),
      dateOfDiagnosis: formatEmpty(item.dateOfDiagnosis),
      physicianName: formatEmpty(item.physicianName),
      patientNumber: formatOrNA(item.patientNumber, (v) => v.toUpperCase()),
      physicianEmail: formatEmpty(item.physicianEmail),
    };
  });

  // Helper to escape CSV values
  const escapeCSV = (value) => {
    const str = String(value);
    return `"${str.replace(/"/g, '""')}"`;
  };

  // Create CSV rows
  const csvRows = transformedData.map(row =>
    Object.values(row).map(value => escapeCSV(value)).join(',')
  );

  // Combine headers and rows
  const csvHeader = headers + '\n';
  const csvContent = csvHeader + csvRows.join('\n');

  // Add UTF-8 BOM to ensure special characters display correctly
  const BOM = '\uFEFF';
  const csvWithBOM = BOM + csvContent;

  // Create a Blob and download
  const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Fetch dashboard statistics for all consented patients
 * @param {Object} options - Query options
 * @param {string} options.timePeriod - Time period (3months, 6months, 12months)
 * @param {string} options.startDate - Custom start date (YYYY-MM-DD) - optional
 * @param {string} options.endDate - Custom end date (YYYY-MM-DD) - optional
 * @returns {Promise<Object>} Dashboard statistics data
 */
export const fetchDashboardStats = async (options = {}) => {
  try {
    const { timePeriod = '12months', startDate, endDate } = options;

    const params = { timePeriod };

    // Add custom date range if provided
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    console.log('[reportService] Fetching dashboard stats with params:', params);

    const response = await apiClient.get('/api/medical-reports/dashboard-stats', { params });

    console.log('[reportService] Received dashboard stats:', {
      success: response.data.success,
      totalPatients: response.data.data?.metadata?.totalPatients,
      totalEvents: response.data.data?.metadata?.totalEvents
    });

    return response.data;
  } catch (error) {
    console.error('Failed to fetch dashboard stats:', error);

    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please login again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. Only medical administrators can access dashboard statistics.');
    } else if (error.response?.status === 404) {
      throw new Error('Dashboard statistics service not found.');
    } else if (error.response?.status === 400) {
      throw new Error(error.response?.data?.message || 'Invalid request parameters.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to fetch dashboard statistics. Please try again.');
    }
  }
};

/**
 * Fetch comprehensive overview data for a specific patient
 * @param {string} patientId - The patient's unique ID
 * @returns {Promise<Object>} Patient overview data
 *
 * Returns:
 * - demographics: { patientNumber, name, email, sex, age, diagnosisDate, typeOfHAE, treatments }
 * - treatmentLocations: [{ location, count, percentage }]
 * - attackLocations: [{ location, count, percentage }]
 * - severityDistribution: [{ severity, label, count, percentage }]
 * - timeBetweenAttacks: { averageDays, totalAttacks, minDays, maxDays }
 * - medicationStats: [{ medicationName, usageCount, totalDose, doseType, averageDose }]
 * - triggerAnalysis: [{ trigger, count, percentage }]
 * - symptomAnalysis: [{ symptom, count, percentage }]
 * - monthlyFrequency: [{ month, attackCount }]
 */
export const fetchPatientOverview = async (patientId) => {
  try {
    if (!patientId) {
      throw new Error('Patient ID is required');
    }

    console.log('[reportService] Fetching patient overview for:', patientId);

    // Call the overview endpoint using the special ID pattern
    const response = await apiClient.get(`/api/medical-patients/${patientId}-overview`);

    console.log('[reportService] Received patient overview:', {
      success: response.data.success,
      patientId: response.data.data?.patientId,
      hasDemo: !!response.data.data?.demographics,
      attackCount: response.data.data?.timeBetweenAttacks?.totalAttacks
    });

    return response.data;
  } catch (error) {
    console.error('Failed to fetch patient overview:', error);

    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please login again.');
    } else if (error.response?.status === 403) {
      const message = error.response?.data?.message || 'Access denied.';
      if (message.includes('consent')) {
        throw new Error('Patient has not provided consent for data access.');
      }
      throw new Error('Access denied. Only medical administrators can access patient overview data.');
    } else if (error.response?.status === 404) {
      throw new Error('Patient not found.');
    } else if (error.response?.status === 400) {
      throw new Error(error.response?.data?.message || 'Invalid patient ID.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to fetch patient overview. Please try again.');
    }
  }
};

/**
 * Fetch patient timeline data for Historical Data tab
 * @param {string} patientId - Patient UUID
 * @param {Object} options - Query options
 * @param {string} options.startDate - Start date (YYYY-MM-DD) - optional
 * @param {string} options.endDate - End date (YYYY-MM-DD) - optional
 * @returns {Promise<Object>} Timeline data
 */
export const fetchPatientTimeline = async (patientId, options = {}) => {
  try {
    if (!patientId) {
      throw new Error('Patient ID is required');
    }

    const { startDate, endDate } = options;
    const params = {};

    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    console.log(`[reportService] Fetching timeline for patient ${patientId}`, params);

    const response = await apiClient.get(
      `/api/medical-patients/${patientId}-timeline`,
      { params }
    );

    if (!response.data || !response.data.success) {
      throw new Error('Invalid response from server');
    }

    console.log(`[reportService] Timeline data received:`, {
      prophylaxis: response.data.data.prophylaxisTreatments?.length,
      acute: response.data.data.acuteTreatments?.length,
      attacks: response.data.data.attackIntensity?.length
    });

    return response.data.data;

  } catch (error) {
    console.error('[reportService] Error fetching timeline:', error);

    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please log in again.');
    } else if (error.response?.status === 403) {
      throw new Error('You do not have permission to view this patient\'s timeline.');
    } else if (error.response?.status === 404) {
      throw new Error('Patient not found or has no timeline data.');
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || 'Invalid request parameters.');
    }

    throw new Error(error.message || 'Failed to load patient timeline data');
  }
};


/**
 * Fetch medication group list with usage counts
 * @returns {Promise<Array>} Array of medications with usage totals
 *
 * Returns:
 * [
 *   { _id: "medication-name", total: number }
 * ]
 */
export const fetchMedicationGroupList = async () => {
  try {
    console.log("[reportService] Fetching medication group list");

    const response = await apiClient.get("/api/medical-reports/medication-group-list");

    console.log("[reportService] Received medication group list:", {
      count: response.data?.length || 0
    });

    return response.data || [];
  } catch (error) {
    console.error("Failed to fetch medication group list:", error);

    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error("Authentication required. Please log in again.");
    } else if (error.response?.status === 403) {
      throw new Error("Access denied. Only medical administrators can access medication list.");
    } else if (error.response?.status === 404) {
      throw new Error("Medication list endpoint not found.");
    } else if (error.response?.status === 400) {
      throw new Error(error.response?.data?.message || "Invalid request parameters.");
    } else {
      throw new Error(error.response?.data?.message || "Failed to fetch medication list. Please try again.");
    }
  }
};


/**
 * Fetch infusion statistics for medications
 * GET /api/medical-reports/infusion-stats
 *
 * @param {Object} params - Query parameters
 * @param {string} params.startDate - Start date (YYYY-MM-DD)
 * @param {string} params.endDate - End date (YYYY-MM-DD)
 * @param {string[]} params.medications - Optional array of medication names to filter
 * @param {string[]} params.patientIds - Optional array of patient IDs to filter
 *
 * @returns {Promise<Object>} Infusion statistics
 */
export const fetchInfusionStats = async ({ startDate, endDate, medications = [], patientIds = [] }) => {
  try {
    console.log("[reportService] Fetching infusion stats:", { startDate, endDate, medications, patientIds });

    const params = {
      startDate,
      endDate
    };

    if (medications && medications.length > 0) {
      params.medications = medications;
    }

    if (patientIds && patientIds.length > 0) {
      params.patientIds = patientIds;
    }

    const response = await apiClient.get("/api/medical-reports/infusion-stats", { params });

    console.log("[reportService] Received infusion stats:", {
      totalPatients: response.data?.totalPatients || 0,
      resultsCount: response.data?.results?.length || 0
    });

    return response.data || { totalPatients: 0, filterValue: 0, results: [] };
  } catch (error) {
    console.error("Failed to fetch infusion stats:", error);

    if (error.response?.status === 401) {
      throw new Error("Authentication required. Please log in again.");
    } else if (error.response?.status === 403) {
      throw new Error("Access denied. Only medical administrators can access infusion statistics.");
    } else if (error.response?.status === 404) {
      throw new Error("Infusion stats endpoint not found.");
    } else if (error.response?.status === 400) {
      throw new Error(error.response?.data?.message || "Invalid request parameters.");
    } else {
      throw new Error(error.response?.data?.message || "Failed to fetch infusion statistics. Please try again.");
    }
  }
};

